import { useEffect, useRef, MutableRefObject } from 'react';
import mapboxgl from 'mapbox-gl';
import { MAP_CONFIG } from '../components/map/config';
import { MapError } from '../types/mapTypes';
import { checkMapboxConfig } from '../utils/envLoader';

export const useMapInitialization = (
  mapContainer: MutableRefObject<HTMLDivElement | null>,
  mapInstance: MutableRefObject<mapboxgl.Map | null>,
  onError: (error: MapError) => void,
  onMapClick: (e: mapboxgl.MapMouseEvent) => void
): void => {
  // Utiliser une ref pour stocker onMapClick et éviter que le useEffect se re-déclenche
  const onMapClickRef = useRef(onMapClick);
  
  // Mettre à jour la ref quand onMapClick change
  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);
  
  useEffect(() => {
    console.log('🗺️ useMapInitialization effect:', {
      hasContainer: !!mapContainer.current,
      hasInstance: !!mapInstance.current,
      mapInstanceValue: mapInstance.current
    });
    
    // Si l'instance existe déjà, ne rien faire
    if (mapInstance.current) {
      console.log('✅ Map instance existe déjà, skip initialization');
      return;
    }
    
    if (!mapContainer.current) {
      console.log('❌ Pas de container, skip initialization');
      return;
    }

    const initializeMap = async () => {
      // Détection des variables d'environnement - Logs en mode développement uniquement
      if (process.env.NODE_ENV === 'development') {
        console.log("🔑 VARIABLES D'ENVIRONNEMENT DISPONIBLES:", {
          'NODE_ENV': process.env.NODE_ENV,
          'PUBLIC_URL': process.env.PUBLIC_URL,
          'REACT_APP_DEFINED': process.env.REACT_APP_DEFINED !== undefined
        });
        
        // Logs spécifiques pour les variables Mapbox
        console.log("🔑 MAPBOX TOKEN (env):", 
          process.env.REACT_APP_MAPBOX_TOKEN ? 
          `${process.env.REACT_APP_MAPBOX_TOKEN.substring(0, 8)}...` : 
          'Non défini');
        console.log("🔑 MAPBOX STYLE (env):", process.env.REACT_APP_MAPBOX_STYLE || 'Non défini');
        
        // Vérifier les variables dans window (parfois injectées par le serveur)
        console.log("🔑 MAPBOX TOKEN (window):", 
          (window as any).REACT_APP_MAPBOX_TOKEN ? 
          `${(window as any).REACT_APP_MAPBOX_TOKEN.substring(0, 8)}...` : 
          'Non défini');
        console.log("🔑 MAPBOX STYLE (window):", (window as any).REACT_APP_MAPBOX_STYLE || 'Non défini');
      }

      // Utiliser l'utilitaire de configuration Mapbox pour récupérer les variables
      const mapboxConfig = checkMapboxConfig();
      const token = mapboxConfig.token;
      const style = mapboxConfig.style;
      
      // Afficher des informations sur la configuration (développement uniquement)
      if (process.env.NODE_ENV === 'development') {
        if (mapboxConfig.isUsingPublicToken) {
          console.warn('⚠️ AVERTISSEMENT: Utilisation du token public de démonstration');
        }
        
        if (mapboxConfig.isUsingPublicStyle) {
          console.warn('⚠️ AVERTISSEMENT: Utilisation du style public standard');
        }
        
        console.log("📍 TOKEN UTILISÉ:", `${token.substring(0, 8)}...`);
        console.log("📍 STYLE UTILISÉ:", style);
        console.log("📍 CONFIGURATION MAPBOX:", mapboxConfig);
      }

      // Vérifier si le conteneur existe et a des dimensions
      if (mapContainer.current) {
        const containerRect = mapContainer.current.getBoundingClientRect();
        if (process.env.NODE_ENV === 'development') {
          console.log("📍 DIMENSIONS DU CONTENEUR:", {
            width: containerRect.width,
            height: containerRect.height,
            visible: containerRect.width > 0 && containerRect.height > 0
          });
        }
      } else {
        console.error("📍 CONTENEUR DE CARTE INTROUVABLE");
        return; // Arrêter l'initialisation si le conteneur n'existe pas
      }

      mapboxgl.accessToken = token;
      
      try {
        // Vérifier à nouveau si mapContainer.current existe pour éviter toute race condition
        if (!mapContainer.current) {
          onError({
            message: 'Élément conteneur de la carte non disponible',
            code: 'MAP_CONTAINER_ERROR',
            severity: 'error'
          });
          return;
        }
        
        // Forcer l'utilisation du style standard Mapbox qui fonctionne
        const styleToUse = 'mapbox://styles/mapbox/streets-v11';
        
        // Forcer l'affichage du conteneur avec des styles explicites
        if (mapContainer.current) {
          // Appliquer des styles forcés pour garantir la visibilité
          const containerStyles = {
            display: 'block',
            width: '100%',
            height: '100%',
            minHeight: '500px',
            position: 'relative',
            visibility: 'visible',
            opacity: '1',
            overflow: 'visible',
            backgroundColor: '#f0f0f0',
            zIndex: '1'
          };
          
          // Appliquer tous les styles au conteneur
          Object.entries(containerStyles).forEach(([property, value]) => {
            mapContainer.current!.style[property as any] = value;
          });
          
          console.log('📍 Styles forcés appliqués au conteneur');
        }
        
        console.log('📍 Style Mapbox utilisé:', styleToUse);
        
        // Créer la carte avec le style configuré
        const map = new mapboxgl.Map({
          container: mapContainer.current,
          style: styleToUse,
          center: MAP_CONFIG.defaultCenter,
          zoom: MAP_CONFIG.defaultZoom,
          minZoom: MAP_CONFIG.minZoom,
          maxZoom: MAP_CONFIG.maxZoom,
          renderWorldCopies: false,
          trackResize: true,
          fadeDuration: 0,
          preserveDrawingBuffer: true
        });

        // Optimiser le rendu pendant le déplacement
        map.on('movestart', () => {
          if (map.getZoom() < 13) { // Ajuster selon vos besoins
            map.getCanvas().style.imageRendering = 'pixelated';
          }
        });

        map.on('moveend', () => {
          map.getCanvas().style.imageRendering = 'auto';
        });

        mapInstance.current = map;
        console.log("🧪 MAP INSTANCE CREATED:", map);

        map.on('load', () => {
          console.log('🧪 MAP LOADED SUCCESSFULLY');
          console.log('🧪 MAP CONTAINER:', mapContainer.current);
          console.log('🧪 MAP CANVAS:', document.querySelector('.mapboxgl-canvas'));
          
          // Vérifier les dimensions du canvas après chargement
          const canvas = document.querySelector('.mapboxgl-canvas') as HTMLCanvasElement;
          if (canvas) {
            console.log('🧪 CANVAS DIMENSIONS:', {
              width: canvas.width,
              height: canvas.height,
              style: {
                width: canvas.style.width,
                height: canvas.style.height
              }
            });
          }
          
          // Ajouter les contrôles de navigation avec des options minimales
          map.addControl(
            new mapboxgl.NavigationControl({
              showCompass: false, // Désactiver la boussole pour tous les modes
              showZoom: true,
              visualizePitch: false
            }),
            'top-left' // Placer les contrôles en haut à gauche pour éviter la bande à droite
          );
          
          // Fonction pour nettoyer les éléments d'attribution
          const cleanupAttributionElements = () => {
            // Supprimer complètement les éléments d'attribution
            const attribElements = document.querySelectorAll('.mapboxgl-ctrl-attrib, .mapboxgl-ctrl-attrib-button');
            attribElements.forEach(el => {
              el.remove();
            });
          };
          
          // Nettoyer immédiatement
          cleanupAttributionElements();
          
          // Configurer un observateur de mutations pour supprimer les éléments d'attribution s'ils réapparaissent
          const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              if (mutation.addedNodes.length > 0) {
                // Vérifier si de nouveaux éléments d'attribution ont été ajoutés
                cleanupAttributionElements();
              }
            }
          });
          
          // Observer le conteneur de la carte pour les changements s'il existe
          if (mapContainer.current) {
            observer.observe(mapContainer.current, { childList: true, subtree: true });
          }
          
          // Ajouter un écouteur pour les changements de taille d'écran
          const handleResize = () => {
            // Nettoyer les éléments d'attribution après chaque redimensionnement
            setTimeout(cleanupAttributionElements, 100);
          };
          
          window.addEventListener('resize', handleResize);
          
          // Nettoyer l'observateur et l'écouteur lors du démontage
          map.on('remove', () => {
            observer.disconnect();
            window.removeEventListener('resize', handleResize);
          });
          // Émettre un événement personnalisé quand la carte est prête
          const mapReadyEvent = new CustomEvent('mapready', { detail: { map } });
          window.dispatchEvent(mapReadyEvent);
        });
        
        map.on('error', (e) => {
          console.error("🧪 MAPBOX ERROR:", e);
          onError({
            message: `Erreur Mapbox: ${e.error?.message || 'Erreur inconnue'}`,
            code: 'MAPBOX_ERROR',
            severity: 'error'
          });
        });

        // Gestionnaire de clic sur la carte
        map.on('click', (e) => {
          const target = e.originalEvent.target as HTMLElement;
          const isMarkerClick = target.closest('.mapboxgl-marker');
          
          if (!isMarkerClick) {
            onMapClickRef.current(e);
          }
        });

      } catch (error) {
        onError({
          message: 'Erreur lors de l\'initialisation de la carte',
          code: 'MAP_INIT_ERROR',
          severity: 'error'
        });
      }
    };

    initializeMap();

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []); // Pas de dépendances - s'exécute une seule fois au montage
};

